import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { Card } from './Cards';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import Converters from './Converters';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

interface BeerStyleDetail {
  id: number;
  styleId: string;
  name: string;
  translatedName: string | null;
  type: string;
  category: {
    categoryId: string;
    name: string;
    translatedName: string | null;
    description: string;
    translatedDescription: string | null;
  };
  ogMin: number | null;
  ogMax: number | null;
  fgMin: number | null;
  fgMax: number | null;
  ibuMin: number | null;
  ibuMax: number | null;
  abvMin: number | null;
  abvMax: number | null;
  colorMin: number | null;
  colorMax: number | null;
  overallImpression: string | null;
  translatedOverallImpression: string | null;
  aroma: string | null;
  translatedAroma: string | null;
  appearance: string | null;
  translatedAppearance: string | null;
  flavor: string | null;
  translatedFlavor: string | null;
  mouthfeel: string | null;
  translatedMouthfeel: string | null;
  comments: string | null;
  translatedComments: string | null;
  history: string | null;
  translatedHistory: string | null;
  ingredients: string | null;
  translatedIngredients: string | null;
  styleComparison: string | null;
  translatedStyleComparison: string | null;
  examples: string | null;
  tags: string | null;
  styleGuide: string;
}

function formatRange(min: number | null, max: number | null, decimals = 1): string {
  if (min == null && max == null) return '—';
  if (min != null && max != null) return `${min.toFixed(decimals)} – ${max.toFixed(decimals)}`;
  if (min != null) return `${min.toFixed(decimals)}+`;
  return `≤ ${max!.toFixed(decimals)}`;
}

function sgToBlg(sg: number | null): number | null {
  if (sg == null) return null;
  return Converters.gravityToPlato(sg);
}

function srmToEbc(srm: number | null): number | null {
  if (srm == null) return null;
  return Converters.srmToEbc(srm);
}

function Section({ title, content }: { title: string; content: string | null }) {
  if (!content) return null;
  return (
    <Card title={title}>
      <p className="text-sm text-foreground leading-relaxed">{content}</p>
    </Card>
  );
}

function StatItem({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="text-center">
      <p className="text-xs text-muted-foreground font-medium mb-1">{label}</p>
      <p className="text-lg font-mono font-semibold text-foreground">
        {value}
        {unit && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
      </p>
    </div>
  );
}

export default function BeerStyleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [style, setStyle] = useState<BeerStyleDetail | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    const lang = i18n.resolvedLanguage ?? i18n.language;
    fetch(`/api/styles/${id}?lang=${lang}`)
      .then(res => {
        if (!res.ok) throw new Error(t('styles.styleNotFoundError'));
        return res.json();
      })
      .then((result: BeerStyleDetail) => {
        setStyle(result);
        setIsLoaded(true);
        document.title = `${t('common.appName')} - ${result.translatedName || result.name}`;
      })
      .catch((err: Error) => {
        setError(err);
        setIsLoaded(true);
      });
  }, [id, i18n.language]);

  if (error) return <p className="text-destructive text-sm">{t('common.error', { message: error.message })}</p>;
  if (!isLoaded) return <p className="text-muted-foreground text-sm">{t('common.loading')}</p>;
  if (!style) return <p className="text-destructive text-sm">{t('styles.styleNotFound')}</p>;

  const tr = (translated: string | null, en: string | null) => translated || en;

  return (
    <div>
      <Link
        to="/styles"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('styles.backToList')}
      </Link>

      <Card title={
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="font-mono text-xs">{style.styleId}</Badge>
          <span>{style.translatedName || style.name}</span>
        </div>
      }>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>{t('styles.category')}: <strong className="text-foreground">{style.category.translatedName || style.category.name}</strong></span>
            <span>•</span>
            <span>{t('styles.type')}: <strong className="text-foreground">{style.type}</strong></span>
            <span>•</span>
            <span>{t('styles.guide')}: <strong className="text-foreground">{style.styleGuide}</strong></span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 py-4 border-y">
            <StatItem
              label="BLG (OG)"
              value={formatRange(sgToBlg(style.ogMin), sgToBlg(style.ogMax))}
              unit="°BLG"
            />
            <StatItem
              label="BLG (FG)"
              value={formatRange(sgToBlg(style.fgMin), sgToBlg(style.fgMax))}
              unit="°BLG"
            />
            <StatItem
              label="ABV"
              value={formatRange(style.abvMin, style.abvMax)}
              unit="%"
            />
            <StatItem
              label={t('styles.colIbu')}
              value={formatRange(style.ibuMin, style.ibuMax, 0)}
              unit="IBU"
            />
            <StatItem
              label={t('styles.colEbc')}
              value={formatRange(srmToEbc(style.colorMin), srmToEbc(style.colorMax), 0)}
              unit="EBC"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 pb-2 text-xs text-muted-foreground">
            <StatItem
              label="SG (OG)"
              value={formatRange(style.ogMin, style.ogMax, 3)}
            />
            <StatItem
              label="SG (FG)"
              value={formatRange(style.fgMin, style.fgMax, 3)}
            />
            <div />
            <div />
            <StatItem
              label="Color (SRM)"
              value={formatRange(style.colorMin, style.colorMax, 0)}
              unit="SRM"
            />
          </div>

          {style.tags && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {style.tags.split(',').map(tag => (
                <Badge key={tag.trim()} variant="outline" className="text-xs">
                  {tag.trim()}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Section title={t('styles.sectionOverallImpression')} content={tr(style.translatedOverallImpression, style.overallImpression)} />
      <Section title={t('styles.sectionAroma')} content={tr(style.translatedAroma, style.aroma)} />
      <Section title={t('styles.sectionAppearance')} content={tr(style.translatedAppearance, style.appearance)} />
      <Section title={t('styles.sectionFlavor')} content={tr(style.translatedFlavor, style.flavor)} />
      <Section title={t('styles.sectionMouthfeel')} content={tr(style.translatedMouthfeel, style.mouthfeel)} />
      <Section title={t('styles.sectionComments')} content={tr(style.translatedComments, style.comments)} />
      <Section title={t('styles.sectionHistory')} content={tr(style.translatedHistory, style.history)} />
      <Section title={t('styles.sectionStyleComparison')} content={tr(style.translatedStyleComparison, style.styleComparison)} />
      <Section title={t('styles.sectionIngredients')} content={tr(style.translatedIngredients, style.ingredients)} />

      {style.examples && (
        <Card title={t('styles.sectionExamples')}>
          <p className="text-sm text-foreground">{style.examples}</p>
        </Card>
      )}
    </div>
  );
}
